import { Component, computed, DestroyRef, inject, OnInit, signal, Signal } from '@angular/core';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { ArrayDataSource, CardModule, ComponentsModule, FormFieldModule, IconModule, InputOption, ResultDataSource, SnackBarService, StepperItem } from '@mastechnicaegroup/carelinelive-ui';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { createProviderCareOptionsFormGroup, createProviderFormGroup, createProviderRegionFormGroup, ProviderSingleResponse, RegionOptionsToStore, StoreProviderRequest } from '@lib/api/models/provider';
import { ProviderService } from '@lib/provider/provider.service';
import { Router } from '@angular/router';
import { createAddressFormGroup } from '@lib/api/models/address';
import { createContactNumberFormGroup, StoreContactNumberRequest } from '@lib/api/models/contact-numbers';
import { toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { CareOptionName, CareTypeName } from '@lib/api/models/setting';
import { combineLatest, map, shareReplay, startWith, take, tap } from 'rxjs';
import { SettingsService } from '@lib/setting/settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RegionService } from '@lib/region/region.service';
import { RegionOption } from '@lib/api/models/region';
import { ZoneOption } from '@lib/api/models/zone';
import { PriorityOption } from '@lib/api/models/priorities';
import { FormValidationService } from '@shared/services/form-validation.service';
import { ToggleComponent } from "../../../shared/components/toggle/toggle.component";

export enum Step {
    provider = 'provider',
    contact = 'contact',
    regionAndZones = 'region-and-zones',
    careOptions = 'care-options',
}

@Component({
    selector: 'app-providers-add-page',
    imports: [
    HeaderComponent,
    CardModule,
    FormsModule,
    IconModule,
    ReactiveFormsModule,
    FormFieldModule,
    ComponentsModule,
    NgIf,
    NgForOf,
    AsyncPipe,
    ToggleComponent
],
    templateUrl: './providers-add-page.component.html',
    standalone: true,
    styleUrl: './providers-add-page.component.scss'
})

export class ProvidersAddPageComponent implements OnInit {

    protected readonly Step = Step;
    protected readonly addressFormGroup = createAddressFormGroup();
    protected readonly contactNumbersFormGroup = createContactNumberFormGroup();
    protected readonly providerDetailsFormGroup = createProviderFormGroup();

    private readonly _providerService = inject(ProviderService);
    private readonly _settingService = inject(SettingsService);
    private readonly _regionService = inject(RegionService);
    private readonly _router = inject(Router);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _fb = inject(FormBuilder);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _formValidationService = inject(FormValidationService);

    private providerId = '';

    includePriceFlags: boolean[] = [];
    includePriceSelectedKeys: number[] = [];

    protected contactNumbersForm = this._fb.group({
        contactNumbers: this._fb.array([createContactNumberFormGroup()])
    });

    protected careOptionsForm = this._fb.group({
        careOptions: this._fb.array([createProviderCareOptionsFormGroup()])
    });

    protected regionOptionsForm = this._fb.group({
        regionOptions: this._fb.array([createProviderRegionFormGroup()])
    });

    protected readonly completedSteps = signal<Set<Step>>(new Set());

    protected readonly steps: Signal<StepperItem[]> = computed((): StepperItem[] => {
        const completedSteps = this.completedSteps();

        return [
            {
                title: 'Provider',
                id: Step.provider,
                status: completedSteps.has(Step.provider) ? 'checked' : undefined
            },
            {
                title: 'Contact',
                id: Step.contact,
                status: completedSteps.has(Step.contact) ? 'checked' : undefined
            },
            {
                title: 'Region and Zones',
                id: Step.regionAndZones,
                status: completedSteps.has(Step.regionAndZones) ? 'checked' : undefined
            },
            {
                title: 'Care Options',
                id: Step.careOptions,
                status: completedSteps.has(Step.careOptions) ? 'checked' : undefined
            }
        ];
    });

    addProviderRequest = new SendableRequest<ProviderSingleResponse, StoreProviderRequest>({
        request: payload => this._providerService.createProvider(payload!),

        onSend: () => {
            this.providerDetailsFormGroup.disable();
        },
        

        onSuccess: response => {

            this.providerId = response.data.id;

            let contactNumbers = this.contactNumbersForm.value.contactNumbers;
            if (contactNumbers) {
                if (contactNumbers.length === 1) {
                    if (contactNumbers[0].number === '' && contactNumbers[0].type === null) {
                        contactNumbers = [];
                    }
                }
                this.addContactNumberToProvider(contactNumbers as StoreContactNumberRequest[]);
            }

            const careOptions = this.careOptionsForm.value.careOptions;
          
            if (careOptions) {
                const careOptionsToSend = (careOptions ?? [])
                .map(option => {
                  const careOpt = option.care_option as CareOptionName | null;
              
                  if (!careOpt?.id){
                    return null;
                  } 
              
                  return {
                    care_option_id: careOpt.id,
                    include_price: option.include_price ?? false,
                    price: option.price ?? 0
                  };
                })
                .filter((item): item is { care_option_id: string; include_price: boolean; price: number } => item !== null);

                this.addCareOptionsToProvider(careOptionsToSend);
            }

            const regionsOptions = this.regionOptionsForm.value.regionOptions;
            if (regionsOptions) {
                const regionOptionsRequest = (this.regionOptionsForm.value.regionOptions??[]).map(regionOptions => ({
                    id: regionOptions.region?.id ?? '',
                    zones: (regionOptions.zones ?? []).map(zone => zone.id),
                    priority: regionOptions.priority?.id ?? ''
                }));
                this.addRegionOptionsToProvider(regionOptionsRequest);
            }

            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: 'Provider added successfully.'
            });

            this._router.navigate(['/providers', this.providerId]);
        }
    });

    updateProviderRequest = new SendableRequest<ProviderSingleResponse, StoreProviderRequest>({
        request: payload => this._providerService.updateProvider(this.providerId, payload!),

        onSuccess: () => {
            return;
        }
    });

    protected readonly data = {
        allCareTypes$: this._settingService.getAllPossibleCareTypes().pipe(
            take(1),
            shareReplay()
        ),
        allCareOptions$: this._settingService.getAllPossibleCareOptions().pipe(
            take(1),
            shareReplay()
        ),
        allRegions$: this._regionService.getAllRegions({ activePriorities: true }).pipe(
            take(1),
            shareReplay()
        )
    };

    careOptionDataSources: Record<string, ArrayDataSource<CareOptionName>> = {};
    zoneDataSources: Record<string, ArrayDataSource<ZoneOption>> = {};
    priorityDataSources: Record<string, ArrayDataSource<PriorityOption>> = {};

    //Set up the careOptionDataSources for each possible care type
    subscriptionCareOpts = combineLatest([
        this.data.allCareTypes$,
        this.data.allCareOptions$
    ]).pipe(
        takeUntilDestroyed(this._destroyRef),
        take(1),
        shareReplay(),
        tap(([careTypes, careOptions]) => {
            careTypes.data.forEach((careType: CareTypeName) => {
                this.careOptionDataSources[careType.id] =
                    new ArrayDataSource({
                        items:
                            careOptions.data.filter(
                                (careOption: CareOptionName) => careOption.care_type_id === careType.id
                            ).map((careOption: CareOptionName) => {
                                return {
                                    value: careOption,
                                    label: careOption.name
                                };
                            })
                    });
            });
        })
    ).subscribe();

    protected searchableCareTypeDataSource = new ResultDataSource<CareTypeName>({
        source: combineLatest([
            this.data.allCareTypes$,
            this.careOptionsForm.valueChanges.pipe(startWith({
                careOptions: []
            }))
        ]).pipe(
            map(([response]): InputOption<CareTypeName>[] => {
                return (response?.data ?? '')
                    .map(careType => ({
                        value: careType,
                        label: careType.name
                    }));
            }),
            toResult(),
            shareReplay(1)
        ),
    });

    // setup the regionOptionsDataSources for each possible region
    subscriptionRegions = this.data.allRegions$.pipe(
        takeUntilDestroyed(this._destroyRef),
        take(1),
        shareReplay(),
        tap(regions => {
            regions.data.forEach((region: RegionOption) => {
                this.zoneDataSources[region.id] =
                    new ArrayDataSource({
                        items:
                            region.zones.map((zone: ZoneOption) => {
                                return {
                                    value: zone,
                                    label: zone.name
                                };
                            })
                    });

                this.priorityDataSources[region.id] =
                    new ArrayDataSource({
                        items:
                            region.priorities
                            .map((priority: PriorityOption) => {
                                return {
                                    value: priority,
                                    label: priority.name
                                };
                            })
                    });
            });
        })
    ).subscribe();

    protected searchableRegionDataSource = new ResultDataSource<RegionOption>({
        source: combineLatest([this.data.allRegions$, this.regionOptionsForm.valueChanges.pipe(startWith({regionOptions: []}))]).pipe(
            map(([response, formChanges]): InputOption<RegionOption>[] => {
                const usedRegions = (formChanges?.regionOptions ?? []).map((formData) => formData.region?.id);
                return (response?.data ?? [])
                    .filter(region => {
                        return !usedRegions.includes(region.id);
                    })
                    .map(region => ({
                        value: region,
                        label: region.name,
                        testId: region.id
                    }));
            }),
            toResult(),
            shareReplay(1)
        ),
        searchable: true,
        compareValue: (a, b) => a.id === b.id
    });

    protected readonly nextStep: Signal<StepperItem> = computed(() => {
        const steps = this.steps();
        const currentStepId = this.currentStepId();

        const currentIndex = steps.findIndex(step => step.id === currentStepId);
        return steps[Math.min(steps.length - 1, currentIndex + 1)];
    });

    protected readonly previousStep: Signal<StepperItem> = computed(() => {
        const steps = this.steps();
        const currentStepId = this.currentStepId();

        const currentIndex = steps.findIndex(step => step.id === currentStepId);
        return steps[Math.max(0, currentIndex - 1)];
    });

    protected currentStepId = signal<Step>(Step.provider);

    protected goToNextStep(): void {
        const updatedCompletedSteps = new Set(this.completedSteps());
        updatedCompletedSteps.add(this.currentStepId());
        this.completedSteps.set(updatedCompletedSteps);

        this.currentStepId.set(this.nextStep().id as Step);
    }

    protected goToPreviousStep(): void {
        const updatedCompletedSteps = new Set(this.completedSteps());
        updatedCompletedSteps.delete(this.currentStepId());
        this.completedSteps.set(updatedCompletedSteps);

        this.currentStepId.set(this.previousStep().id as Step);
    }

    get contactNumbersFormArray(): FormArray {
        return this.contactNumbersForm.get('contactNumbers') as FormArray;
    }

    addContactNumber() {
        this.contactNumbersFormArray.push(createContactNumberFormGroup());
    }

    addContactNumberToProvider(contactNumbers: StoreContactNumberRequest[]) {
        const providerData: StoreProviderRequest = {
            contact_numbers_to_store: contactNumbers
        };

        this.updateProviderRequest.send(providerData);
    }

    addCareOptionsToProvider(careOptions: { care_option_id: string; include_price: boolean; price: number }[]) {
        const providerData: StoreProviderRequest = {
            care_options_to_store: careOptions
        };

        this.updateProviderRequest.send(providerData);
    }

    addRegionOptionsToProvider(regionOptions: RegionOptionsToStore[]) {
        const providerData: StoreProviderRequest = {
            region_options_to_store: regionOptions
        };

        this.updateProviderRequest.send(providerData);
    }

    removeContactNumber(index: number) {
        this.contactNumbersFormArray.removeAt(index);

        if (this.contactNumbersFormArray.length === 0) {
            this.addContactNumber();
        }
    }

    get careOptionsFormArray(): FormArray {
        return this.careOptionsForm.get('careOptions') as FormArray;
    }

    addCareOption() {
        const newGroup = createProviderCareOptionsFormGroup();
        this.careOptionsFormArray.push(newGroup);
      
        this.subscribeToCareOptionChange(newGroup, this.careOptionsFormArray.length - 1);
      }
      

    removeCareOption(index: number) {
        this.careOptionsFormArray.removeAt(index);

        this.includePriceSelectedKeys = this.includePriceSelectedKeys
        .filter(i => i !== index)
        .map(i => i > index ? i - 1 : i);

        if (this.careOptionsFormArray.length === 0) {
            this.addCareOption();
        }
    }

    get regionOptionsFormArray(): FormArray {
        return this.regionOptionsForm.get('regionOptions') as FormArray;
    }

    addRegion() {
        // make prior option disabled
        this.regionOptionsFormArray.push(createProviderRegionFormGroup());
    }

    removeRegion(i: number) {
        this.regionOptionsFormArray.removeAt(i);

        if (this.regionOptionsFormArray.controls.length === 0) {
            this.addRegion();
        }
    }

    createProvider(): void {
        if (this.addProviderRequest.loading()) {
            return;
        }

        if (!this.providerDetailsFormGroup.valid) {
            this._formValidationService.markFormGroupTouched(this.providerDetailsFormGroup);

            this._formValidationService.showFormErrors(this.providerDetailsFormGroup, true);

            return;
        }

        const providerData: StoreProviderRequest = {
            reference: this.providerDetailsFormGroup.value.reference ?? '',
            name: this.providerDetailsFormGroup.value.name ?? '',
            email: this.providerDetailsFormGroup.value.email ?? '',
            status: true,
            cqc_location_id: this.providerDetailsFormGroup.value.cqc_location_id ?? '',
            address: {
                street_1: this.addressFormGroup.value.street_1 ?? '',
                street_2: this.addressFormGroup.value.street_2 ?? '',
                town: this.addressFormGroup.value.town ?? '',
                county: this.addressFormGroup.value.county ?? '',
                post_code: this.addressFormGroup.value.post_code ?? '',
                country: this.addressFormGroup.value.country ?? '',
                lat: this.addressFormGroup.value.lat ?? 0,
                lng: this.addressFormGroup.value.lng ?? 0,
                hard_to_reach: this.addressFormGroup.value.hard_to_reach ?? false,
                additional_comments: this.addressFormGroup.value.additional_comments ?? ''
            }
        };

        this.addProviderRequest.send(providerData);
    }

    cancel(): void {
        this._router.navigate(['/providers'])
            .then(success => {
                if (!success) {
                    this._snackBar.open({
                        colour: 'error',
                        icon: 'error',
                        title: 'Failed to navigate to the providers index page.'
                    });
                    return;
                }
            });
    }

    ngOnInit() {
        this.careOptionsFormArray.controls.forEach((control, index) => {
          this.subscribeToCareOptionChange(control as FormGroup, index);
        });
        this.updateIncludePriceFlags();
      }

      private subscribeToCareOptionChange(group: FormGroup, index: number) {
        group.get('care_option')?.valueChanges.subscribe(value => {
          if(value.include_price) {
            this.includePriceSelectedKeys.push(index);
          } else {
            this.includePriceSelectedKeys = this.includePriceSelectedKeys
            .filter(i => i !== index)
            .map(i => i > index ? i - 1 : i);
          }
        });
      }

      updateIncludePriceFlags() {
        this.includePriceFlags = (this.careOptionsForm.get('careOptions') as FormArray).controls.map(
          (group: AbstractControl) => group.get('include_price')?.value ?? false
        );
      }

      onIncludePriceToggle(checked: boolean, index: number): void {
        const control = (this.careOptionsForm.get('careOptions') as FormArray)
          .at(index)
          .get('include_price');
      
        control?.setValue(checked);
        this.updateIncludePriceFlags();
      }
      
      getIncludePrice(i: number): boolean {
        return (
          (this.careOptionsForm.get('careOptions') as FormArray)
            ?.at(i)
            ?.get('include_price')
            ?.value ?? false
        );
      }
      
    }

    
